import { SITE_URLS } from '@constants';
import { TFunction } from 'i18next';

export const COOKIE_CONSENT_STORAGE_NAME = 'bc-cookie-preferences';
export const COOKIE_CONSENT_ELEMENT_ID = 'bc-cookie-manager';

export interface KlaroService {
    name: string;
    purposes: string[];
    required?: boolean;
    default?: boolean;
    onlyOnce?: boolean;
    cookies?: (string | RegExp)[];
    translations?: Record<string, { title: string; description: string }>;
    callback?: (consent: boolean) => void;
}

export interface KlaroConfig {
    version: number;
    elementID: string;
    storageName: string;
    storageMethod: 'localStorage' | 'cookie';
    cookieExpiresAfterDays: number;
    lang: string;
    acceptAll: boolean;
    hideDeclineAll: boolean;
    default: boolean;
    mustConsent: boolean;
    showNoticeTitle: boolean;
    translations: Record<string, Record<string, unknown>>;
    services: KlaroService[];
    privacyPolicy: Record<string, string>;
    purposeOrder: string[];
}

interface BuildCookieConsentConfigParams {
    languages: string[];
    fallbackLanguage: string;
    currentLanguage: string;
    getTranslator: (language: string) => TFunction;
    onPreferenceChange: (service: string, consent: boolean) => void;
    policyUrlResolver: (language?: string) => string;
}

const SERVICE_KEYS = ['analytics'] as const;
type ServiceKey = (typeof SERVICE_KEYS)[number];

const buildServiceTranslations = (languages: string[], getTranslator: (language: string) => TFunction, key: ServiceKey) => {
    return languages.reduce<Record<string, { title: string; description: string }>>((translations, language) => {
        const translate = getTranslator(language);
        translations[language] = {
            title: translate(`cookie-manager.services.${key}.title`),
            description: translate(`cookie-manager.services.${key}.description`),
        };
        return translations;
    }, {});
};

const buildTranslations = (languages: string[], getTranslator: (language: string) => TFunction) => {
    return languages.reduce<Record<string, Record<string, unknown>>>((acc, language) => {
        const translate = getTranslator(language);
        acc[language] = {
            acceptAll: translate('cookie-manager.actions.accept-all'),
            acceptSelected: translate('cookie-manager.actions.accept-selected'),
            decline: translate('cookie-manager.actions.reject'),
            ok: translate('cookie-manager.actions.ok'),
            save: translate('cookie-manager.actions.save'),
            close: translate('cookie-manager.actions.close'),
            poweredBy: translate('cookie-manager.powered-by'),
            consentNotice: {
                title: translate('cookie-manager.notice.title'),
                description: translate('cookie-manager.notice.description', {
                    purposes: '{purposes}',
                    privacyPolicy: '{privacyPolicy}',
                }),
                learnMore: translate('cookie-manager.notice.learn-more'),
                changeDescription: translate('cookie-manager.notice.changes'),
            },
            consentModal: {
                title: translate('cookie-manager.modal.title'),
                description: translate('cookie-manager.modal.description'),
            },
            purposes: {
                analytics: {
                    title: translate('cookie-manager.purposes.analytics.title'),
                    description: translate('cookie-manager.purposes.analytics.description'),
                },
            },
            privacyPolicy: {
                name: translate('cookie-manager.privacy-policy.link-label'),
                text: translate('cookie-manager.privacy-policy.description'),
            },
        };
        return acc;
    }, {});
};

const buildServices = (
    languages: string[],
    getTranslator: (language: string) => TFunction,
    onPreferenceChange: (service: string, consent: boolean) => void
): KlaroService[] => {
    return [
        {
            name: 'analytics-suite',
            purposes: ['analytics'],
            default: true,
            cookies: ['_ga', '_gid', /^_ga_/],
            translations: buildServiceTranslations(languages, getTranslator, 'analytics'),
            callback: (consent) => onPreferenceChange('analytics', consent),
        },
    ];
};

export const buildCookieConsentConfig = ({
    languages,
    fallbackLanguage,
    currentLanguage,
    getTranslator,
    onPreferenceChange,
    policyUrlResolver,
}: BuildCookieConsentConfigParams): KlaroConfig => {
    const uniqueLanguages = Array.from(new Set(languages));

    const privacyPolicy = uniqueLanguages.reduce<Record<string, string>>((map, language) => {
        map[language] = policyUrlResolver(language);
        return map;
    }, {});
    privacyPolicy.default = policyUrlResolver(fallbackLanguage);

    return {
        version: 2,
        elementID: COOKIE_CONSENT_ELEMENT_ID,
        storageName: COOKIE_CONSENT_STORAGE_NAME,
        storageMethod: 'localStorage',
        cookieExpiresAfterDays: 180,
        lang: currentLanguage,
        acceptAll: true,
        hideDeclineAll: false,
        default: false,
        mustConsent: false,
        showNoticeTitle: true,
        translations: buildTranslations(uniqueLanguages, getTranslator),
        services: buildServices(uniqueLanguages, getTranslator, onPreferenceChange),
        privacyPolicy,
        purposeOrder: ['analytics'],
    };
};

export const buildPolicyUrl = (language: string | undefined, fallbackLanguage: string) => {
    const policyPath = SITE_URLS.policy.replace(':pageKey', 'privacy-policy');
    const isDefault = !language || language === fallbackLanguage;
    return isDefault ? `/${policyPath}` : `/${language}/${policyPath}`;
};
